/**
 * CommsProvider — session identity, the `say` relay and inbound Telegram routing.
 *
 * Outbound flow
 *   visitor types `say hello` (terminal or the bottom uplink bar)
 *     -> POST /api/say { text, sender_name, session_id }
 *     -> backend calls Telegram sendMessage and records the returned
 *        message_id against this session
 *     -> UI confirms "transmitted into the void"
 *
 * Inbound flow
 *   a group member replies to that message on Telegram
 *     -> backend long-poller sees reply_to_message, routes it to this session
 *     -> GET /api/inbox/{session_id} drains it
 *     -> every subscriber (terminal transcript, uplink badge, Moony) is notified
 *
 * Replies are published through a tiny listener set so components can render
 * them wherever the visitor is looking, without prop drilling.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { HISTORY_LIMIT, HISTORY_POLL_MS, INBOX_POLL_MS } from './config';
import {
  createSession,
  fetchInbox,
  fetchTelegramHistory,
  fetchTelegramStatus,
  logCommand,
  sendSay,
} from './api';

const CommsContext = createContext(null);

export function CommsProvider({ children }) {
  const [session, setSession] = useState({ id: null, username: '' });
  const [relayStatus, setRelayStatus] = useState({
    enabled: false,
    pollerRunning: false,
    checked: false,
  });
  const [unreadReplies, setUnreadReplies] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [history, setHistory] = useState({ messages: [], threadId: null, loaded: false });

  const listeners = useRef(new Set());
  const sessionIdRef = useRef(null);
  // The terminal registers a sink here so the bottom bar can write into the
  // main transcript (R4) and drive its optimistic pending line (R5).
  const terminalSink = useRef(null);
  const usernameRef = useRef('');

  useEffect(() => {
    sessionIdRef.current = session.id;
  }, [session.id]);

  useEffect(() => {
    usernameRef.current = session.username;
  }, [session.username]);

  /** Subscribe to inbound Telegram replies. Returns an unsubscribe function. */
  const subscribe = useCallback((listener) => {
    listeners.current.add(listener);
    return () => listeners.current.delete(listener);
  }, []);

  const publish = useCallback((reply) => {
    listeners.current.forEach((listener) => {
      try {
        listener(reply);
      } catch (error) {
        console.error('relay listener failed', error);
      }
    });
  }, []);

  const markRepliesRead = useCallback(() => setUnreadReplies(0), []);

  // ------------------------------------------------------- terminal mirroring

  /** Called by TerminalSection with { append, beginPending, resolvePending, clear }. */
  const registerTerminal = useCallback((sink) => {
    terminalSink.current = sink;
    return () => {
      if (terminalSink.current === sink) terminalSink.current = null;
    };
  }, []);

  const getTerminal = useCallback(() => terminalSink.current, []);

  /** Create (or re-create) the backend session for a username. */
  const startSession = useCallback(async (username) => {
    const name = String(username || '').trim();
    if (!name) return null;
    usernameRef.current = name;
    setSession((prev) => (prev.username === name ? prev : { ...prev, username: name }));
    try {
      const id = await createSession(name);
      setSession({ id, username: name });
      return id;
    } catch (error) {
      // Offline: keep the name so the terminal still works locally.
      console.error('Failed to create session:', error);
      setSession({ id: null, username: name });
      return null;
    }
  }, []);

  const recordCommand = useCallback(async (command) => {
    const id = sessionIdRef.current;
    const name = usernameRef.current;
    if (!id || !name) return;
    try {
      await logCommand({ sessionId: id, username: name, command });
    } catch {
      // Logging is best-effort; never surface it to the visitor.
    }
  }, []);

  /**
   * Relay one message into the Telegram group.
   * Returns { ok, messageId, error, relayEnabled } for the caller to render.
   */
  const sendingRef = useRef(false);

  const say = useCallback(
    async (text) => {
      const message = String(text || '').trim();
      if (!message || sendingRef.current) {
        return { ok: false, messageId: null, error: 'nothing to send', relayEnabled: true };
      }

      sendingRef.current = true;
      setIsSending(true);
      try {
        const result = await sendSay({
          text: message,
          senderName: usernameRef.current || 'Anonymous',
          sessionId: sessionIdRef.current,
        });
        // The backend may assign an anonymous session id when we have none yet.
        if (result.sessionId && result.sessionId !== sessionIdRef.current) {
          sessionIdRef.current = result.sessionId;
          setSession((prev) => ({ ...prev, id: result.sessionId }));
        }
        return result;
      } catch (error) {
        console.error('say failed', error);
        return {
          ok: false,
          messageId: null,
          error: 'the uplink is down',
          relayEnabled: relayStatus.enabled,
        };
      } finally {
        sendingRef.current = false;
        setIsSending(false);
      }
    },
    [relayStatus.enabled],
  );

  // ------------------------------------------------ relay capability probe
  useEffect(() => {
    let cancelled = false;
    fetchTelegramStatus()
      .then((status) => {
        if (!cancelled) setRelayStatus({ ...status, checked: true });
      })
      .catch(() => {
        if (!cancelled) setRelayStatus({ enabled: false, pollerRunning: false, checked: true });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ------------------------------------------------- inbound reply polling
  useEffect(() => {
    const id = session.id;
    if (!id) return undefined;

    let cancelled = false;

    const poll = async () => {
      try {
        const replies = await fetchInbox(id);
        if (cancelled || !replies.length) return;
        replies.forEach((reply) => publish(reply));
        setUnreadReplies((count) => count + replies.length);
      } catch {
        // Backend unreachable: the next tick retries.
      }
    };

    poll();
    const timer = setInterval(poll, INBOX_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [session.id, publish]);

  // ---------------------------------------------- group transcript polling
  useEffect(() => {
    if (!relayStatus.enabled) return undefined;

    let cancelled = false;

    const poll = async () => {
      try {
        const next = await fetchTelegramHistory(HISTORY_LIMIT);
        if (!cancelled) setHistory({ ...next, loaded: true });
      } catch {
        if (!cancelled) setHistory((prev) => ({ ...prev, loaded: true }));
      }
    };

    poll();
    const timer = setInterval(poll, HISTORY_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [relayStatus.enabled]);

  const value = useMemo(
    () => ({
      session,
      sessionId: session.id,
      username: session.username,
      startSession,
      recordCommand,
      registerTerminal,
      getTerminal,
      history,
      say,
      isSending,
      relayStatus,
      subscribe,
      unreadReplies,
      markRepliesRead,
    }),
    [
      session,
      startSession,
      recordCommand,
      registerTerminal,
      getTerminal,
      history,
      say,
      isSending,
      relayStatus,
      subscribe,
      unreadReplies,
      markRepliesRead,
    ],
  );

  return <CommsContext.Provider value={value}>{children}</CommsContext.Provider>;
}

export function useComms() {
  const context = useContext(CommsContext);
  if (!context) throw new Error('useComms must be used inside <CommsProvider>');
  return context;
}
